import find from 'lodash/find';

import type { AssignmentDraft } from 'bundles/author-assignment/core/types/types';
import type { ItemPrefix, ItemTypeAdditionalParameters } from 'bundles/author-course/constants/ItemTypes';
import ItemTypes, {
  ASSIGNMENT,
  COACH_ITEM_DIALOGUE,
  COACH_ITEM_ROLE_PLAY,
  PRACTICE_ASSIGNMENT,
  PRACTICE_TEAM_ASSIGNMENT,
  STAFF_GRADED,
  TEAM_ASSIGNMENT,
} from 'bundles/author-course/constants/ItemTypes';
import { getItemTypePrefixTranslation } from 'bundles/author-course/utils/ItemTypeUtilsPrefixes';
import { COACH_ITEM_TYPENAME_OVERRIDE } from 'bundles/coach-item/common/constants';
import { COACH_ITEM_SHARED_ITEM_TYPENAME } from 'bundles/item/constants/constants';
import type { ItemType, ItemTypeLabel, ItemTypeName } from 'bundles/item/types/ItemType';

const itemPrefixMap: Record<string, ItemPrefix> = {
  lecture: 'Video',
  notebook: 'Notebook',
  supplement: 'Reading',
  reading: 'Reading',
  discussionPrompt: 'Discussion Prompt',
  gradedDiscussionPrompt: 'Graded Discussion Prompt',
  exam: 'Quiz',
  quiz: 'Quiz',
  assessOpenSinglePage: 'Quiz',
  peer: 'Peer Review',
  peerReview: 'Peer Review',
  closedPeer: 'Peer Review',
  gradedPeer: 'Peer Review',
  phasedPeer: 'Peer Review',
  programming: 'Programming Assignment',
  programmingAssignment: 'Programming Assignment',
  gradedProgramming: 'Programming Assignment',
  ungradedProgramming: 'Programming Assignment',
  ungradedLti: 'App Item',
  gradedLti: 'App Item',
  ltiTool: 'App Item',
  staffGraded: 'Assignment',
  teammateReview: 'Teammate Review',
  ungradedWidget: 'Ungraded Plugin',
  dialogue: 'Dialogue',
  gradedWidget: 'Graded Plugin',
  widget: 'Ungraded Plugin',
  workspaceLauncher: 'Workspace',
  ungradedLab: 'Ungraded Lab',
  gradedLab: 'Graded Lab',
  ungradedPlaceholder: 'Placeholder',
  gradedPlaceholder: 'Placeholder',
  wiseFlow: 'WISEflow Item',
  coach: 'Dialogue',
  gradedCoach: 'Graded Role Play',
};

function itemPrefixFromItemTypeName(
  key: ItemType | string,
  itemTypeAdditionalParameters?: ItemTypeAdditionalParameters
): ItemPrefix {
  if (key === 'staffGraded') {
    return ASSIGNMENT.prefix;
  }
  if (key === 'ungradedAssignment') {
    return PRACTICE_ASSIGNMENT.prefix;
  }

  if (key === COACH_ITEM_SHARED_ITEM_TYPENAME) {
    if (itemTypeAdditionalParameters?.customDisplayTypenameOverride === COACH_ITEM_TYPENAME_OVERRIDE.ROLE_PLAY) {
      return COACH_ITEM_ROLE_PLAY.prefix;
    }

    return COACH_ITEM_DIALOGUE.prefix;
  }
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return itemPrefixMap[key];
}

function getItemTypePrefixTranslationByTypeName(
  key: ItemType | string,
  itemTypeAdditionalParameters?: ItemTypeAdditionalParameters
): string {
  return key
    ? getItemTypePrefixTranslation(
        itemPrefixFromItemTypeName(key, itemTypeAdditionalParameters),
        itemTypeAdditionalParameters
      ) || ''
    : '';
}

function getItemTypeFromPrefix(prefix: string, itemTypeAdditionalParameters?: ItemTypeAdditionalParameters) {
  const { isTeamAssignment, isGraded } = (itemTypeAdditionalParameters as ItemTypeAdditionalParameters) || {};
  if (prefix === 'Assignment') {
    if (isGraded === undefined || isGraded) {
      return isTeamAssignment ? TEAM_ASSIGNMENT : ASSIGNMENT;
    } else {
      return isTeamAssignment ? PRACTICE_TEAM_ASSIGNMENT : PRACTICE_ASSIGNMENT;
    }
  }

  return find(ItemTypes, (type) => prefix === type.prefix);
}

export const getAssignmentItemType = (isTeamAssignment?: boolean, typeName?: ItemType) => {
  if (typeName === 'staffGraded') {
    return isTeamAssignment ? TEAM_ASSIGNMENT : ASSIGNMENT;
  }
  if (typeName === 'ungradedAssignment') {
    return isTeamAssignment ? PRACTICE_TEAM_ASSIGNMENT : PRACTICE_ASSIGNMENT;
  }

  return STAFF_GRADED;
};

export const getItemTypeFromTypeName = (
  itemTypeName: ItemType,
  isTeamAssignment?: boolean
): (typeof ItemTypes)[keyof typeof ItemTypes] => {
  if (itemTypeName === 'staffGraded' || itemTypeName === 'ungradedAssignment') {
    return getAssignmentItemType(isTeamAssignment, itemTypeName);
  }

  // @ts-expect-error Result can be undefined
  return find(ItemTypes, (type) => type.typeName === itemTypeName);
};

function getItemRouteNameByTypeName(typeName: ItemType, itemIsGraded?: boolean): string {
  // special handling for GDP until we merge graded/ungraded DP types
  const updatedTypeName = typeName === 'discussionPrompt' && itemIsGraded ? 'gradedDiscussionPrompt' : typeName;

  let itemType = find(ItemTypes, (type) => type.typeName === updatedTypeName);

  // handle consolidated/updated item type names returned by authoring change log
  if (!itemType) {
    itemType = find(ItemTypes, (type) => type.prefix === itemPrefixFromItemTypeName(updatedTypeName));
  }

  return itemType?.routeName ?? '';
}

export const getAssignmentItemTypeLabel = (draft: AssignmentDraft): ItemTypeLabel => {
  const isGradedAssignment = draft?.configuration?.isGraded;
  const isTeamSubmission = draft?.configuration?.isTeamSubmissionEnabled;

  if (isGradedAssignment) {
    return isTeamSubmission ? 'GRADED_TEAM_ASSIGNMENT' : 'GRADED_INDIVIDUAL_ASSIGNMENT';
  } else {
    return isTeamSubmission ? 'PRACTICE_TEAM_ASSIGNMENT' : 'PRACTICE_INDIVIDUAL_ASSIGNMENT';
  }
};

export const getAssignmentDraftItemProperties = (itemTypeLabel?: ItemTypeLabel) => {
  if (!itemTypeLabel) {
    return undefined;
  }

  const isGradedAssignment =
    itemTypeLabel === 'GRADED_INDIVIDUAL_ASSIGNMENT' || itemTypeLabel === 'GRADED_TEAM_ASSIGNMENT';
  const isTeamAssignment = itemTypeLabel === 'GRADED_TEAM_ASSIGNMENT' || itemTypeLabel === 'PRACTICE_TEAM_ASSIGNMENT';
  const draftTypeName: ItemTypeName = isGradedAssignment ? 'staffGraded' : 'ungradedAssignment';

  return {
    draftTypeName,
    isGraded: isGradedAssignment,
    isTeamSubmissionEnabled: isTeamAssignment,
  };
};

const exported = {
  itemPrefixFromItemTypeName,
  getItemTypePrefixTranslationByTypeName,
  getItemRouteNameByTypeName,
  getItemTypeFromPrefix,
};

export default exported;
export {
  itemPrefixFromItemTypeName,
  getItemTypePrefixTranslationByTypeName,
  getItemRouteNameByTypeName,
  getItemTypeFromPrefix,
};
